import { useCallback, useEffect, useState } from "react";
import { type MqCreatePayload, type MqItem, api } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

const STATUS_COLOR: Record<string, string> = {
  new: "#60a5fa",
  working: "#fbbf24",
  completed: "#34d399",
  failed: "#f87171",
  cancelled: "#71717a",
};

const BUILT_IN_ACTIONS = [
  { value: "lock_computer", label: "Lock Computer", defaultTarget: "desktop" },
  { value: "run_script", label: "Run Script", defaultTarget: "desktop" },
  { value: "push_notification", label: "Push Notification", defaultTarget: "desktop" },
  { value: "open_url", label: "Open URL", defaultTarget: "desktop" },
];

function fmtDate(ts: number) {
  return new Date(ts * 1000).toLocaleString();
}

function fmtAge(ts: number) {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Create form ───────────────────────────────────────────────────────────────

function CreateForm({ syncKey, onCreated }: { syncKey: string; onCreated: () => void }) {
  const [action, setAction] = useState("lock_computer");
  const [customAction, setCustomAction] = useState("");
  const [params, setParams] = useState("{}");
  const [target, setTarget] = useState("desktop");
  const [priority, setPriority] = useState(5);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isCustom = action === "__custom__";
  const resolvedAction = isCustom ? customAction.trim() : action;

  async function submit() {
    if (!resolvedAction) return;
    let parsedParams: unknown = {};
    try {
      parsedParams = JSON.parse(params);
    } catch {
      setError("params must be valid JSON");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload: MqCreatePayload = {
        action: resolvedAction,
        params: parsedParams,
        target,
        priority,
        key: key.trim() || undefined,
      };
      await api.mq.create(syncKey, payload);
      setParams("{}");
      setKey("");
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create item");
    } finally {
      setBusy(false);
    }
  }

  function onActionChange(v: string) {
    setAction(v);
    const preset = BUILT_IN_ACTIONS.find((a) => a.value === v);
    if (preset) setTarget(preset.defaultTarget);
  }

  return (
    <div className="settings-section">
      <h2>Enqueue action</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Action</label>
          <select value={action} onChange={(e) => onActionChange(e.target.value)} style={{ width: "100%" }}>
            {BUILT_IN_ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          {isCustom && (
            <input
              placeholder="custom.action_name"
              value={customAction}
              onChange={(e) => setCustomAction(e.target.value)}
              style={{ width: "100%", marginTop: 6, fontFamily: "monospace", fontSize: 12 }}
            />
          )}
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Target</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: "100%" }}>
            <option value="desktop">desktop</option>
            <option value="server">server</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
            Priority <span style={{ color: "#555" }}>(1=high, 10=low)</span>
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
            Key <span style={{ color: "#555" }}>(optional dedup)</span>
          </label>
          <input
            placeholder="idempotency key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
          />
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
          Params (JSON)
        </label>
        <textarea
          rows={3}
          value={params}
          onChange={(e) => setParams(e.target.value)}
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
          spellCheck={false}
        />
      </div>
      {error && <p style={{ color: "#f87171", fontSize: 12, marginBottom: 6 }}>{error}</p>}
      <button type="button" onClick={submit} disabled={busy || !resolvedAction}>
        {busy ? "Enqueueing…" : "Enqueue"}
      </button>
    </div>
  );
}

// ── Item row ──────────────────────────────────────────────────────────────────

function ItemRow({ item, syncKey, onReload }: { item: MqItem; syncKey: string; onReload: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); } catch { /* ignore */ } finally { setBusy(false); onReload(); }
  }

  const canCancel = item.status === "new" || item.status === "failed";
  const canRetry = item.status === "failed";
  const canDelete = item.status !== "working";

  return (
    <div className="settings-section" style={{ marginBottom: 8, padding: "10px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: STATUS_COLOR[item.status] ?? "#888",
            flexShrink: 0,
          }}
        />
        <code style={{ fontFamily: "monospace", fontSize: 13, flex: 1 }}>{item.action}</code>
        <span
          style={{
            fontSize: 11,
            color: STATUS_COLOR[item.status] ?? "#888",
            fontWeight: 600,
            minWidth: 70,
            textAlign: "right",
          }}
        >
          {item.status}
        </span>
        <span style={{ fontSize: 11, color: "#555", minWidth: 60, textAlign: "right" }}>
          P{item.priority}
        </span>
        <span style={{ fontSize: 11, color: "#555", minWidth: 70, textAlign: "right" }}>
          {fmtAge(item.createdAt)}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{ fontSize: 11, padding: "2px 8px" }}
        >
          {expanded ? "Less" : "More"}
        </button>
        {canRetry && (
          <button
            type="button"
            onClick={() => act(() => api.mq.retry(syncKey, item.id))}
            disabled={busy}
            style={{ fontSize: 11, padding: "2px 8px" }}
          >
            Retry
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            onClick={() => act(() => api.mq.cancel(syncKey, item.id))}
            disabled={busy}
            style={{ fontSize: 11, padding: "2px 8px" }}
          >
            Cancel
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => act(() => api.mq.delete(syncKey, item.id))}
            disabled={busy}
            className="clip-icon-btn"
            title="Delete"
            aria-label="Delete item"
          >
            🗑
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 10, fontSize: 12, color: "#aaa" }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "3px 8px" }}>
            <span style={{ color: "#555" }}>ID</span>
            <code style={{ fontFamily: "monospace", fontSize: 11 }}>{item.id}</code>
            <span style={{ color: "#555" }}>Target</span>
            <span>{item.target}</span>
            <span style={{ color: "#555" }}>Try count</span>
            <span>{item.tryCount}</span>
            {item.key && (
              <>
                <span style={{ color: "#555" }}>Key</span>
                <code style={{ fontFamily: "monospace", fontSize: 11 }}>{item.key}</code>
              </>
            )}
            <span style={{ color: "#555" }}>Created</span>
            <span>{fmtDate(item.createdAt)}</span>
            <span style={{ color: "#555" }}>Updated</span>
            <span>{fmtDate(item.updatedAt)}</span>
            {item.completedAt && (
              <>
                <span style={{ color: "#555" }}>Completed</span>
                <span>{fmtDate(item.completedAt)}</span>
              </>
            )}
          </div>
          {item.params && item.params !== "{}" && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: "#555", fontSize: 11 }}>Params</span>
              <pre style={{ margin: "4px 0 0", fontSize: 11, color: "#ccc", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {item.params}
              </pre>
            </div>
          )}
          {item.error && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: "#f87171", fontSize: 11 }}>Error</span>
              <pre style={{ margin: "4px 0 0", fontSize: 11, color: "#f87171", whiteSpace: "pre-wrap" }}>
                {item.error}
              </pre>
            </div>
          )}
          {item.result && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: "#34d399", fontSize: 11 }}>Result</span>
              <pre style={{ margin: "4px 0 0", fontSize: 11, color: "#ccc", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {item.result}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Pending", value: "new,working" },
  { label: "Failed", value: "failed" },
  { label: "Done", value: "completed,cancelled" },
];

export default function MessageQueue({ syncKey }: Props) {
  const [items, setItems] = useState<MqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.mq.list(syncKey, statusFilter ? { status: statusFilter } : undefined);
      setItems(res.items);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [syncKey, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 5s when there are active items
  useEffect(() => {
    if (!autoRefresh) return;
    const hasActive = items.some((i) => i.status === "new" || i.status === "working");
    if (!hasActive) return;
    const t = window.setInterval(load, 5000);
    return () => window.clearInterval(t);
  }, [autoRefresh, items, load]);

  const counts = {
    new: items.filter((i) => i.status === "new").length,
    working: items.filter((i) => i.status === "working").length,
    failed: items.filter((i) => i.status === "failed").length,
    completed: items.filter((i) => i.status === "completed").length,
  };

  return (
    <div className="page">
      <h1>Message Queue</h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
        Queue actions for connected desktop clients or the server. Built-in actions include{" "}
        <code>lock_computer</code>, <code>run_script</code>, <code>push_notification</code>, and{" "}
        <code>open_url</code>. Plugins can register additional action handlers on the desktop.
      </p>

      <div className="stat-grid" style={{ marginBottom: 20 }}>
        {Object.entries(counts).map(([s, n]) => (
          <div key={s} className="stat-card">
            <div className="stat-card__value" style={{ color: STATUS_COLOR[s] }}>{n}</div>
            <div className="stat-card__label">{s}</div>
          </div>
        ))}
      </div>

      <CreateForm syncKey={syncKey} onCreated={load} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 10px" }}>
        <h2 style={{ margin: 0 }}>Queue ({items.length})</h2>
        <div style={{ display: "flex", gap: 6 }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              style={{
                fontSize: 11,
                padding: "2px 10px",
                opacity: statusFilter === f.value ? 1 : 0.5,
                fontWeight: statusFilter === f.value ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label style={{ marginLeft: "auto", fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh
        </label>
        <button type="button" onClick={load} style={{ fontSize: 11, padding: "2px 10px" }}>
          Refresh
        </button>
      </div>

      {loading && items.length === 0 ? (
        <p style={{ color: "#666" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="empty">No queue items{statusFilter ? " matching filter" : ""}.</p>
      ) : (
        items.map((item) => (
          <ItemRow key={item.id} item={item} syncKey={syncKey} onReload={load} />
        ))
      )}
    </div>
  );
}
